import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Button from '../../components/ui/Button'
import { useLogout } from '../../auth/useLogout'
import type { ProxyPersona } from './api/proxyPersonaApi'
import { useProxySwitch } from './useProxySwitch'
import { personaLabelWithAuthority } from './personaLabel'
import { notifyApiError } from '../../api/errorNotifier'
import './proxy.css'

interface Props {
  /**
   * Seçilebilir yöneticiler — {@code useAssistantAutoPersona}'dan **prop olarak** gelir.
   *
   * <p>Bileşen kendi {@code useQuery}'sini açmıyor: açtığında aynı anahtar için <b>ikinci bir GET</b>
   * üretiyordu (push kapısı review'ü ölçtü). Önbellek doluydu ama {@code staleTime: 0} +
   * {@code refetchOnMount} varsayılanları yeni observer'ı tazelemeye gönderiyordu.
   */
  personas: ProxyPersona[]
}

/**
 * <b>Birden çok yöneticinin asistanı olan, kendi rolü olmayan kullanıcıya açılışta gösterilen seçim
 * ekranı</b> (126, kullanıcı kararı 21.10.2026).
 *
 * <p><b>Çözdüğü sorun ölçüldü:</b> o kullanıcıda "Kendim" sunulmuyor (FR-017 istisnası) ve otomatik
 * geçiş <b>tam olarak bir</b> persona arıyor — ikisi birden olunca kullanıcı <i>bomboş</i> bir
 * uygulamaya düşüyordu. Kullanıcı bunu bildirdi: *"bir asistana iki yetki verildiği zaman yine kendi
 * profili açık geliyor … sonuçta hâlâ kendi rolü yoktu"*.
 *
 * <p><b>Neden tahmin etmiyoruz:</b> rastgele (ya da "ilk") personayı seçmek, kullanıcıyı <b>yanlış
 * kişinin verisiyle</b> çalıştırmak olurdu; Tam Yetkide o kişi farkında olmadan yanlış yöneticinin
 * adına görev açıp düzenleyebilirdi. Bir tık sormak, o riski tamamen kaldırıyor ve kullanıcıyı da boş
 * ekranda bırakmıyor.
 *
 * <p><b>Neden tam ekran, pop-up değil:</b> arkasında dimlenecek bir içerik <b>yok</b> — kabuk zaten
 * boş olurdu. Kapatılabilir bir pop-up ise kullanıcıyı yine boş ekranda bırakırdı.
 *
 * <p><b>ÇIKIŞ DÜĞMESİ ZORUNLU</b> (push kapısı review'ü 21.10.2026). İlk yazımda yoktu ve javadoc
 * *"çıkışı kimlik menüsündeki Çıkış'tır"* diyordu — <b>yanlıştı</b>: bu bileşen {@code AppLayout}'un
 * <i>yerine</i> çiziliyor, yani Topbar da kimlik menüsü de mount edilmiyor. Ölçülen sonuç: asistan
 * kaydı sunucuda kaldırılmışsa her iki geçiş de reddedilir ve kullanıcı <b>sonsuza kadar</b> bu
 * ekranda kalırdı; tek çıkış çerezi temizlemekti. Aynı tuzağı {@code AccessRestrictedPage} adıyla
 * çözmüş ({@code useLogout} ile bir aksiyon düğmesi) ve kalıp ondan alındı.
 */
export default function AssistantPersonaChooser({ personas }: Readonly<Props>) {
  const { t } = useTranslation()
  const { enterPersona } = useProxySwitch()
  const logout = useLogout()
  const [pending, setPending] = useState<number | null>(null)

  const choose = (userId: number) => {
    // Çift tık kapısı: ikinci bir geçiş başlatmak ilkinin sonucunu belirsiz kılardı. **Bugün
    // arayüzden ERİŞİLEMİYOR** (ölçüldü: `loading` düğmeyi pasifleştiriyor, `disabled` da diğerlerini
    // — bu satır silindiğinde hiçbir test kırılmıyor). Yine de duruyor: `loading`/`disabled`
    // kalktığında ya da bileşen başka bir yerden çağrıldığında tek koruma bu olurdu, ve bedeli bir
    // karşılaştırma. Testi YOK ve bu bilinçlidir — ölçülemeyen bir davranışa test yazmak, testin
    // kendisini yalan yapardı.
    if (pending != null) return
    setPending(userId)
    // Hata bildirimi BURADA yapılır: `enterPersona` bir TanStack mutation'ı değildir, global
    // `onError` hunisine düşmez — yakalanmazsa kullanıcı neden geçilmediğini hiç anlamazdı.
    // `finally` ile bayrak her iki yolda da temizlenir, yani kullanıcı yeniden deneyebilir.
    enterPersona(userId)
      .catch(notifyApiError)
      .finally(() => setPending(null))
  }

  return (
    <div className="proxy-chooser">
      <h1 id="proxy-chooser-title" className="proxy-chooser-title">
        {t('proxy.chooser.title')}
      </h1>
      <p className="proxy-chooser-desc">{t('proxy.chooser.description')}</p>
      {/* Grup YALNIZ seçenekleri sarar, kökü değil: Çıkış bir yönetici seçimi DEĞİL ve grubun adı
          ("Kimin adına çalışacaksınız?") onu da kapsayınca ekran okuyucuda yanıltıcı oluyordu
          (push kapısı review'ü).

          `<fieldset>`, `role="group"` taşıyan bir `<div>` DEĞİL: ikisi de aynı rolü verir ama yerleşik
          eleman tüm cihazlarda destekleniyor (Sonar S6819 — `ProxySwitchingNotice`'ta `<output>`'a
          geçme sebebiyle aynı kural ailesi). `<legend>` yerine `aria-labelledby` kullanılıyor çünkü
          başlık zaten `<h1>` olarak ekranı adlandırıyor; ikinci bir görünür başlık tekrar olurdu. */}
      <fieldset className="proxy-chooser-list" aria-labelledby="proxy-chooser-title">
        {personas.map((p) => (
          <Button
            key={p.userId}
            label={personaLabelWithAuthority(t, p.displayName, p.kind)}
            icon="pi pi-user"
            variant="secondary"
            className="proxy-chooser-item"
            /* Yalnız TIKLANAN düğme yükleniyor görünür; hepsini birden pasifleştirmek, kullanıcıya
               hangisini seçtiğini unutturur. */
            loading={pending === p.userId}
            disabled={pending != null && pending !== p.userId}
            onClick={() => choose(p.userId)}
          />
        ))}
      </fieldset>
      {/* Çıkış: bu ekranın TEK diğer yolu (bkz. sınıf javadoc'u). Düğmeler reddedilirse kullanıcı
          burada kilitli kalmasın diye `pending` sırasında da etkin bırakılıyor. */}
      <Button
        label={t('auth.logout')}
        icon="pi pi-sign-out"
        text
        className="proxy-chooser-logout"
        onClick={logout}
      />
    </div>
  )
}
